import type { Edge, Node } from '@xyflow/react';
import { useCallback, useEffect, useMemo, useRef, useState, type SetStateAction } from 'react';
import * as Api from '@ViewModels';
import type { BaseNodeData } from '../../../../web/containers/AutomationFlows/types';

import type { TestContact } from '../testing/types';

const EXCLUDED_STEP_TYPES: string[] = [];

/**
 * useCanvasState - Owns the local React state that the canvas needs.
 *
 * This is a clean extraction from AutomationCanvasEditor: all local
 * useState / useMemo / useCallback calls and the nodesRef sync effect.
 */
export function useCanvasState() {
	const [nodes, setNodes] = useState<Node<BaseNodeData>[]>([]);
	const [edges, setEdges] = useState<Edge[]>([]);
	const [testContacts, setTestContacts] = useState<TestContact[]>([]);
	const [isInitialized, setIsInitialized] = useState(false);
	const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
	const [canvasSyncVersion, setCanvasSyncVersionState] = useState(0);
	// The canvas reports its graph back on an animation frame, so a report built before a sync was requested can land
	// after it and overwrite the graph just handed down. Tracking the request synchronously lets those reports be dropped.
	const requestedSyncVersionRef = useRef(0);
	const setCanvasSyncVersion = useCallback((action: SetStateAction<number>) => {
		const nextSyncVersion = typeof action === 'function' ? action(requestedSyncVersionRef.current) : action;
		requestedSyncVersionRef.current = nextSyncVersion;
		setCanvasSyncVersionState(nextSyncVersion);
	}, []);

	// Do not exclude switch globally — eligibility is target-template specific and
	// enforced when adding/dropping steps.
	const excludedStepTypes = EXCLUDED_STEP_TYPES;

	// Ref kept in sync with nodes state for use in callbacks/effects.
	const nodesRef = useRef<Node<BaseNodeData>[]>([]);
	useEffect(() => {
		nodesRef.current = nodes;
	}, [nodes]);

	// Ref kept in sync with edges state for use in callbacks that walk the graph
	// (e.g. behavior-branch ancestor detection without recreating callbacks per edit).
	const edgesRef = useRef<Edge[]>([]);
	useEffect(() => {
		edgesRef.current = edges;
	}, [edges]);

	// Derived: selected node and selected switch node ID.
	const selectedNode = useMemo(() => {
		if (!selectedNodeId) {
			return null;
		}
		return nodes.find(n => n.id === selectedNodeId) ?? null;
	}, [selectedNodeId, nodes]);

	const selectedSwitchNodeId = useMemo(() => {
		if (!selectedNode) {
			return null;
		}
		const data = selectedNode.data;
		if ('stepType' in data && data.stepType === Api.AutomationStepType.Switch) {
			return selectedNode.id;
		}
		return null;
	}, [selectedNode]);

	// Change handlers.
	const handleNodesChange = useCallback((updatedNodes: Node<BaseNodeData>[], graphSyncVersion?: number) => {
		if (graphSyncVersion !== undefined && graphSyncVersion < requestedSyncVersionRef.current) {
			return;
		}
		setNodes(updatedNodes);
	}, []);

	const handleEdgesChange = useCallback((updatedEdges: Edge[], graphSyncVersion?: number) => {
		if (graphSyncVersion !== undefined && graphSyncVersion < requestedSyncVersionRef.current) {
			return;
		}
		setEdges(updatedEdges);
	}, []);

	const handleTestContactsChange = useCallback((nextContacts: SetStateAction<TestContact[]>) => {
		setTestContacts(nextContacts);
	}, []);

	const handleSelectionChange = useCallback((selectedNodeIds: string[]) => {
		setSelectedNodeId(selectedNodeIds.length > 0 ? selectedNodeIds[0] : null);
	}, []);

	return {
		nodes,
		setNodes,
		edges,
		setEdges,
		testContacts,
		setTestContacts,
		isInitialized,
		setIsInitialized,
		selectedNodeId,
		setSelectedNodeId,
		canvasSyncVersion,
		setCanvasSyncVersion,
		excludedStepTypes,
		nodesRef,
		edgesRef,
		selectedNode,
		selectedSwitchNodeId,
		handleNodesChange,
		handleEdgesChange,
		handleTestContactsChange,
		handleSelectionChange,
	};
}
